/**
 * A payment slip to print: the text a card terminal printed, plus what goes in the sheet's header and
 * footer.
 */
export interface PrintableReceipt {
    text: string;
    /** Header fallback when there is no Belegnummer. */
    title?: string;
    /** Belegnummer — names the printed sheet and the saved PDF. */
    beleg?: string;
    date?: string;
    plate?: string;
}

/**
 * Prints a receipt slip from a throwaway iframe holding nothing but the receipt.
 *
 * Printing the live page was the wrong tool. `window.print()` prints the whole document, so it
 * needed @media print rules to hide the app and re-show the modal — and those rules have to fight
 * the modal's own layout: it is a `position: fixed`, flex-centred overlay with `overflow: hidden`
 * and a `max-height`, so the slip printed shifted off the page edge with every line clipped
 * mid-word. Every fix there is another `!important` against a layout that exists for the screen.
 *
 * An iframe has its own document. Nothing to hide, nothing to un-position, no interaction with the
 * app's CSS at all, and its own `<title>` — which is what the browser stamps into the print header,
 * so the sheet no longer says "OperatorApp".
 *
 * (The URL, date and page number in that header belong to the browser's own "Headers and footers"
 * setting. No page can suppress those, by design.)
 *
 * Shared by the operator-app payment lists and the parker-app receipt lookup, so an operator and a
 * parker print the same sheet.
 */
export function printReceiptSlip(receipt: PrintableReceipt): void {
    if (!receipt.text) return;

    const frame = document.createElement('iframe');
    // Off-screen rather than display:none — a display:none iframe has no layout, and browsers
    // will not print it.
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(frame);

    const doc = frame.contentDocument;
    const win = frame.contentWindow;
    if (!doc || !win) {
        frame.remove();
        return;
    }

    doc.open();
    doc.write(`<!doctype html><html><head><title></title><style>
        /* margin:0 is not about spacing — Chromium draws its own header and footer INTO the page
           margin box, so a zero margin leaves them nowhere to render and they disappear, which
           is what makes room for ours below. Browser behaviour rather than a guarantee (Firefox
           keeps its own setting), so the real margin lives on the header/footer and body
           padding: if a browser ignores this, the sheet still comes out correctly inset, just
           with its own header as well as ours. There is no API to switch those off — a page is
           not allowed to hide where a printout came from. */
        @page { margin: 0; }

        body { margin: 0; padding: 26mm 14mm 22mm; background: #fff; color: #000;
               font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }

        /* position:fixed repeats on every printed page, so these behave as running headers on a
           slip long enough to break across sheets — both card copies usually are. */
        .hdr, .ftr { position: fixed; left: 0; right: 0;
                     font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
                     display: flex; justify-content: space-between; align-items: baseline; }
        .hdr { top: 0; padding: 12mm 14mm 3mm; border-bottom: 1px solid #000; }
        .ftr { bottom: 0; padding: 3mm 14mm 12mm; border-top: 1px solid #000;
               font-size: 8px; color: #444; }

        .brand { font-weight: 700; font-size: 12px; letter-spacing: .02em; }
        .doc   { font-size: 9px; color: #444; }

        pre { margin: 0; font: inherit; font-size: 11px; line-height: 1.35;
              white-space: pre-wrap; word-break: break-word; }
    </style></head><body>
        <div class="hdr"><span class="brand">JJames SmartParking GmbH</span><span class="doc" id="hdrDoc"></span></div>
        <pre id="slip"></pre>
        <div class="ftr"><span id="ftrPlate"></span><span id="ftrPrinted"></span></div>
    </body></html>`);
    doc.close();

    // Name the sheet after the Belegnummer — the one number the acquirer, the fiscal record and
    // the parker's own paper slip all agree on, which is what makes a saved PDF findable when a
    // charge is queried months later. Slashes and friends are stripped because a batch-prefixed
    // Beleg ("00/0346") would otherwise be read as a path by the save dialog.
    const fileSafe = (value: string) => value.replace(/[\\/:*?"<>|]/g, '-').trim();
    const printTitle = receipt.beleg
        ? `Beleg ${fileSafe(receipt.beleg)}`
        : receipt.plate ? `Beleg ${fileSafe(receipt.plate)}` : 'Beleg';

    doc.title = printTitle;

    // ...and the PARENT document's title too. Setting only the iframe's is not enough: Chrome
    // takes the suggested PDF filename from the TOP-LEVEL document even when printing an
    // iframe's contentWindow, so the file still saved as "OperatorApp" (index.html's <title>).
    // Swapped for the duration of the print and restored in cleanUp.
    const outerTitle = document.title;
    document.title = printTitle;

    // textContent, never string interpolation: the slip is text a payment terminal printed, and
    // the plate came off a camera. It is not this function's job to decide either contains no
    // markup.
    const set = (id: string, value: string) => {
        const el = doc.getElementById(id);
        if (el) el.textContent = value;
    };

    set('slip', receipt.text);
    set('hdrDoc', [receipt.beleg ? `Beleg-Nr ${receipt.beleg}` : receipt.title, receipt.date]
        .filter(Boolean).join(' · '));
    set('ftrPlate', receipt.plate ? `Kennzeichen ${receipt.plate}` : '');
    set('ftrPrinted', `Gedruckt ${new Date().toLocaleString()}`);

    let cleaned = false;
    const cleanUp = () => {
        if (cleaned) return;
        cleaned = true;
        document.title = outerTitle;
        frame.remove();
    };

    // Chrome blocks inside print() and fires afterprint; Safari fires it asynchronously. The
    // listener handles both, and the timeout means a browser that fires neither still cannot
    // leak the iframe.
    win.addEventListener('afterprint', cleanUp);
    setTimeout(cleanUp, 60_000);

    win.focus();
    win.print();
}
